import api from "./api";

/**
 * Thin client for the gated AI helper endpoints. Every endpoint is additive and
 * returns HTTP 503 when the Claude integration is disabled, so callers MUST
 * fail-silent/safe: catch errors, keep the manual form usable, and never block
 * existing flows on these calls.
 *
 * Each function returns the parsed response body (response.data) and lets the
 * caller decide how to handle rejections.
 */

// Suppress the global axios interceptor toasts — a 503 (Claude disabled) is a
// normal, expected state for these gated calls and must stay fully silent.
const QUIET = { suppress404Toast: true, suppress500Toast: true };

/**
 * Report whether the gated HR assistant is currently available. The floating
 * chat widget calls this on mount and renders nothing unless `enabled` is true.
 *
 * @returns {Promise<{ enabled: boolean }>}
 */
export async function assistantStatus() {
  return (await api.get("/assistant/status", QUIET)).data;
}

/**
 * Send the full chat history to the HR assistant and return its reply. The
 * backend executes every tool as the authenticated user with the app's existing
 * scoped queries — the model only names tools, never widens access.
 *
 * @param {Array<{ role: "user"|"assistant", content: string }>} messages
 * @returns {Promise<{ reply: string, usage: { input: number, output: number, cost_usd: number } }>}
 */
export async function assistantChat(messages) {
  return (await api.post("/assistant/chat", { messages }, QUIET)).data;
}

/**
 * HR-admin only. List the most recent assistant conversations (newest first,
 * last 100) for review/curation. Returns `{ conversations: [] }` when no log
 * file exists yet, so callers can render an empty state without special-casing.
 *
 * @returns {Promise<{ conversations: Array<{ ts: string, employee_code: string|null, question: string, answer: string, used_personal_data: boolean, source: "claude"|"local", model: string, cost_usd: number }> }>}
 */
export async function listAssistantConversations() {
  return (await api.get("/assistant/conversations", QUIET)).data;
}

/**
 * HR-admin only. List the curated GENERAL answers in the local knowledge base
 * that the assistant can serve instantly on-server (no AI cost).
 *
 * @returns {Promise<{ answers: Array<{ question: string, answer: string, doc: string }> }>}
 */
export async function listLearnedAnswers() {
  return (await api.get("/assistant/learned", QUIET)).data;
}

/**
 * HR-admin only. Promote a GENERAL question/answer pair into the local
 * knowledge base. The backend slugs the question into the stored basename and
 * overwrites any existing entry with the same slug.
 *
 * @param {{ question: string, answer: string }} params
 * @returns {Promise<{ ok: boolean, doc: string }>}
 */
export async function createLearnedAnswer({ question, answer }) {
  return (await api.post("/assistant/learned", { question, answer }, QUIET)).data;
}

/**
 * HR-admin only. Delete a curated local answer by its stored basename
 * (e.g. "how-much-leave.json").
 *
 * @param {string} doc
 * @returns {Promise<{ ok: boolean }>}
 */
export async function deleteLearnedAnswer(doc) {
  return (await api.delete(`/assistant/learned/${encodeURIComponent(doc)}`, QUIET)).data;
}
